import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/types';

interface ListItemProps {
  name?: string;
  value?: string | React.ReactNode;
  actions: React.ReactNode[];
}

const ListItem: React.FC<ListItemProps> = ({ name, value, actions }) => {
  return (
    <li className="px-6 grid grid-cols-[180px_1fr_auto] gap-4 py-2 border-b border-neutral-100 last:border-0 hover:bg-neutral-25/50 -mx-2 rounded transition-colors items-baseline">
      <span className="text-sm text-neutral-500 truncate">
        {name || ''}
        {value && ':'}
      </span>
      {value && (
        <span className="text-sm text-neutral-900 wrap-break-word min-w-0">{value || ''}</span>
      )}
      {!value && <span />}
      {actions.length > 0 && (
        <span className="flex items-center gap-0.5">
          {actions.map((ele, i) => (
            <div
              key={i}
              data-testid={`list-item-action-${i}`}
              className="h-7 w-7 flex items-center justify-center text-neutral-400 hover:text-neutral-700 transition-colors"
            >
              {ele}
            </div>
          ))}
        </span>
      )}
    </li>
  );
};

interface ListDataItem {
  id?: string | number;
  name?: string;
  value?: string | React.ReactNode;
}

interface ListProps {
  canEdit?: boolean;
  data?: (ListDataItem | null)[];
  actions?: ((id: string | number) => React.ReactNode)[];
}

const List: React.FC<ListProps> = ({ data, actions, canEdit }) => {
  const permission = useSelector((state: RootState) => state.auth.pagePermission);
  const icons = (id) =>
    (actions || []).map((item, i) => (
      <div key={i} data-testid={`list-action-icon-${i}`}>
        {item(id)}
      </div>
    ));

  if (!data) return null;

  return (
    <ul className="w-full list-none p-0">
      {data.map(
        (ele, i) =>
          ele && (
            <ListItem
              key={i}
              name={ele.name}
              value={ele.value}
              actions={canEdit ?? permission === 2 ? icons(ele.id) : []}
            />
          )
      )}
    </ul>
  );
};

export default List;
